import React, {
  ReactNode,
  createContext,
  useContext,
  useRef,
  useState,
} from "react";
import { Modal, ModalContent } from "./Modal";
import DynamicHeroIcon, { HeroIcons } from "./DynamicHeroIcon";
import { createPortal } from "react-dom";
import { ButtonSolid } from "./Button";
import classNames from "classnames";

export interface AlertOptions {
  icon?: HeroIcons;
  message: string | ReactNode;
  showIcon?: boolean;
  showButton?: boolean;
}

export type AlertState = {
  id: string;
} & AlertOptions;

export type AlertContextProps = (opt: AlertOptions) => Promise<boolean>;

export const AlertContext = createContext<AlertContextProps | null>(null);

export interface AlertProviderProps {
  children: ReactNode;
}

export const useAlert = () => useContext(AlertContext);

type AlertResolver = (state: PromiseLike<boolean> | boolean) => void;

export const AlertProvider = ({ children }: AlertProviderProps) => {
  const [modalState, setModalState] = useState(false);
  const resolver = useRef<AlertResolver>();
  const [message, setMessage] = React.useState<string | ReactNode>("");
  const [showIcon, setShowIcon] = React.useState<boolean | undefined>(true);
  const [showButton, setShowButton] = React.useState<boolean | undefined>(true);
  const [icon, setIcon] = React.useState<HeroIcons | undefined>(undefined);
  // const [intent, setIntent] = React.useState<AlertIntent>("default");
  const confirm = (opt: AlertOptions) => {
    setModalState(true);
    // setIntent(opt.intent);
    setIcon(opt.icon);
    setShowIcon(opt.showIcon);
    setMessage(opt.message);
    setShowButton(opt.showButton);
    return new Promise<boolean>(function (resolve) {
      resolver.current = resolve;
    });
  };

  const handleConfirm = () => {
    resolver && resolver.current && resolver.current(true);
    setModalState(false);
  };

  const contextValue = React.useMemo(() => confirm, []);

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <>
          <AlertModal
            // intent={intent}
            icon={icon}
            message={message}
            showIcon={showIcon}
            showButton={showButton}
            confirmationCallback={handleConfirm}
            open={modalState}
            onOpenChange={setModalState}
          />
        </>,
        document.body
      )}
    </AlertContext.Provider>
  );
};

interface AlertModalProps extends AlertOptions {
  confirmationCallback: () => void;
  open: boolean;
  onOpenChange: (state: boolean) => void;
}

const AlertModal = ({
  confirmationCallback,
  showButton = true,
  showIcon = false,
  message,
  open,
  icon = "ExclamationCircleIcon",
  onOpenChange,
}: AlertModalProps) => {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="relative h-auto w-auto max-w-2xl rounded-lg bg-white shadow dark:bg-gray-900">
        <div className="flex flex-col divide-y dark:divide-gray-700">
          <div
            className={classNames("flex flex-1 flex-col gap-8 p-4", {
              "mb-8": !showButton,
            })}
          >
            <div className="flex flex-row-reverse items-center">
              <button type="button" onClick={confirmationCallback}>
                <DynamicHeroIcon
                  icon="XMarkIcon"
                  className="h-6 w-6 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
                />
              </button>
            </div>
            <div className="flex flex-col items-center justify-center gap-4 px-10">
              {showIcon ? (
                <DynamicHeroIcon
                  icon={icon}
                  type="outline"
                  className="h-12 w-12 text-gray-400 "
                />
              ) : null}

              {typeof message === "string" ? (
                <div className="text-lg text-gray-600 dark:text-gray-400">
                  {message}
                </div>
              ) : (
                message
              )}
            </div>
          </div>
          {showButton ? (
            <div className="mt-8 flex flex-row items-center justify-end gap-4  rounded-b-lg bg-gray-100 p-2 dark:bg-gray-800">
              <ButtonSolid
                size={"sm"}
                label={"Ok"}
                variant={"default"}
                onClick={confirmationCallback}
              />
            </div>
          ) : null}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AlertModal;
